import { Flex } from 'antd';
import { useGetDialogs } from './hooks/use-get-dialogs';
import { DialogsFilters } from './ui/filters/filters';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VirtualizedDialogsList } from './ui/virtualized-dialogs-list/virtualized-dialogs-list';
import { ChatWindow } from './ui/chat-window/chat-window';
import { LoadingState } from './ui/virtualized-dialogs-list/ui/loading-state/loading-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { EmptyState } from './ui/virtualized-dialogs-list/ui/empty-state/empty-state';
import { NoSelectedDialog } from './ui/virtualized-dialogs-list/ui/no-selected-dialog/no-selected-dialog';
import type { StateSnapshot } from 'react-virtuoso';
import type { DialogShortData } from '@shared/api';
import type { ManagerTimerPayload, ManagerTimerState } from './ui/filters/types';
import type { Filter } from '@shared/ui/filters/lib/types';
import { ClientInfo } from './ui/client-info/client-info';
import styles from './styles.module.scss';
import { PageHeader } from '@shared/ui/page-header';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { Routes, buildOrganizationRoute } from '@shared/constants/routes';
import { useGetMessages } from './hooks/use-get-messages';

const getPositiveNumericValue = (raw?: string | null) => {
  if (!raw) return null;

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;

  return parsed;
};

const getDialogAvatarFallback = (dialogId: number) =>
  `https://api.dicebear.com/9.x/avataaars/svg?seed=${dialogId}`;

export const Dialogs = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'dialogs' });
  const navigate = useNavigate();
  const { organizationId: organizationIdParam, dialogId: dialogIdParam } = useParams<{
    organizationId?: string;
    dialogId?: string;
  }>();
  const currentOrganizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const [dialogsSearchValue, setDialogsSearchValue] = useState('');
  const [activeTab, setActiveTab] = useState<'total' | 'leads'>('total');

  const [filters, setFilters] = useState<Filter[]>([]);

  const {
    dialogs,
    hasMore,
    fetchNextData,
    resetPagination,
    isEmpty,
    isLoading,
    isError,
    isRefetching,
    isFetching,
    handleRefetch,
  } = useGetDialogs({ search: dialogsSearchValue, filters, tab: activeTab });

  const [isClientInfoVisible, setIsClientInfoVisible] = useState(true);
  const organizationIdFromPath = useMemo(
    () => getPositiveNumericValue(organizationIdParam),
    [organizationIdParam],
  );
  const dialogIdFromPath = useMemo(() => getPositiveNumericValue(dialogIdParam), [dialogIdParam]);
  const selectedDialogFromList = useMemo(() => {
    if (!dialogIdFromPath) return null;
    return dialogs.find((dialog) => dialog.client_id === dialogIdFromPath) ?? null;
  }, [dialogIdFromPath, dialogs]);
  const dialogMessagesState = useGetMessages({
    clientId: dialogIdFromPath ?? 0,
    skip: !dialogIdFromPath,
  });
  const isDirectDialogRequest = Boolean(dialogIdFromPath && !selectedDialogFromList);
  const isDirectDialogLoading =
    isDirectDialogRequest &&
    (dialogMessagesState.isLoading ||
      dialogMessagesState.isRefetching ||
      dialogMessagesState.isUninitialized);
  const isDirectDialogNotFound =
    isDirectDialogRequest &&
    dialogMessagesState.isError &&
    Number((dialogMessagesState.error as { status?: number } | undefined)?.status) === 404;
  const isDirectDialogError = isDirectDialogRequest && dialogMessagesState.isError;
  const canRenderDialogFromUrl =
    isDirectDialogRequest &&
    !dialogMessagesState.isError &&
    !dialogMessagesState.isLoading &&
    !dialogMessagesState.isUninitialized;
  const activeDialogId =
    selectedDialogFromList?.client_id ?? (canRenderDialogFromUrl ? dialogIdFromPath : null);
  const activeDialogName = selectedDialogFromList?.client_name ?? null;
  const activeDialogAvatar =
    selectedDialogFromList?.client_avatar_url ??
    (activeDialogId ? getDialogAvatarFallback(activeDialogId) : null);

  const [stateSnapshots, setStateSnapshots] = useState<Map<number, StateSnapshot>>(new Map());
  const [managerTimer, setManagerTimer] = useState<ManagerTimerState>({
    formatted: '00:00',
    isActive: false,
    canAddMore: false,
    addExtraTime: null,
    resetTimer: null,
  });

  const handleManagerTimerChange = useCallback(
    (data: ManagerTimerPayload) => {
      if (data.clientId !== activeDialogId) return;

      setManagerTimer({
        formatted: data.formatted,
        isActive: data.isActive,
        canAddMore: data.canAddMore,
        addExtraTime: data.addExtraTime,
        resetTimer: data.resetTimer,
      });
    },
    [activeDialogId],
  );

  const handleTabChange = useCallback(
    (next: 'total' | 'leads') => {
      resetPagination();
      setActiveTab(next);
    },
    [resetPagination],
  );

  const handleFiltersChange = useCallback(
    (next: Filter[]) => {
      resetPagination();
      setFilters(next);
    },
    [resetPagination],
  );

  const navigateToDialogsList = useCallback(
    (replace = true) => {
      const targetOrganizationId = currentOrganizationId || organizationIdFromPath;
      if (!targetOrganizationId) {
        navigate(Routes.dialogs.root, { replace });
        return;
      }

      navigate(buildOrganizationRoute(Routes.organizationScope.dialogs, targetOrganizationId), {
        replace,
      });
    },
    [currentOrganizationId, navigate, organizationIdFromPath],
  );

  const navigateToDialog = useCallback(
    (dialogId: number, organizationId: number, replace = true) => {
      navigate(
        buildOrganizationRoute(Routes.organizationScope.dialog, organizationId, {
          dialogId,
        }),
        { replace },
      );
    },
    [navigate],
  );

  useEffect(() => {
    setManagerTimer({
      formatted: '00:00',
      isActive: false,
      canAddMore: false,
      addExtraTime: null,
      resetTimer: null,
    });
  }, [activeDialogId]);

  const handleSelectDialog = useCallback(
    (dialog: DialogShortData) => {
      if (
        dialog.client_id === dialogIdFromPath &&
        organizationIdFromPath === currentOrganizationId
      ) {
        return;
      }

      navigateToDialog(dialog.client_id, currentOrganizationId);
    },
    [currentOrganizationId, dialogIdFromPath, navigateToDialog, organizationIdFromPath],
  );

  useEffect(() => {
    if (!dialogIdParam || dialogIdFromPath) return;
    navigateToDialogsList();
  }, [dialogIdFromPath, dialogIdParam, navigateToDialogsList]);

  useEffect(() => {
    if (!dialogIdFromPath || !activeDialogId || !currentOrganizationId) return;
    if (organizationIdFromPath === currentOrganizationId) return;

    navigateToDialog(dialogIdFromPath, currentOrganizationId);
  }, [
    activeDialogId,
    currentOrganizationId,
    dialogIdFromPath,
    navigateToDialog,
    organizationIdFromPath,
  ]);

  return (
    <Flex className={styles.root}>
      <Flex vertical gap={24} className={styles.content}>
        <PageHeader title={t('dialogs')} className={styles.header} />

        <Flex gap={12} className={styles.container}>
          <Flex gap={12} vertical className={styles.workspace}>
            <DialogsFilters
              dialogsSearchValue={dialogsSearchValue}
              dialogsSearchHandle={setDialogsSearchValue}
              managerTimer={managerTimer}
              value={filters}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              onSetFiltersFromValue={handleFiltersChange}
            />

            <Flex vertical gap={12} className={styles.main}>
              <Flex gap={12} flex={1} className={styles.columns}>
                <Flex className={styles.dialogsListPane}>
                  {(() => {
                    if (isLoading || isRefetching) {
                      return <LoadingState />;
                    }

                    if (isError) {
                      return <FetchingErrorState refetch={handleRefetch} />;
                    }

                    if (isEmpty) {
                      return <EmptyState isSearch={Boolean(dialogsSearchValue)} />;
                    }

                    return (
                      <VirtualizedDialogsList
                        dialogs={dialogs}
                        selectedDialogId={dialogIdFromPath ?? null}
                        isFetchingMore={isFetching && hasMore}
                        fetchNextData={fetchNextData}
                        onSelect={handleSelectDialog}
                      />
                    );
                  })()}
                </Flex>

                {activeDialogId ? (
                  <Flex className={styles.chatPane} flex={1}>
                    <ChatWindow
                      clientId={activeDialogId}
                      messagesState={dialogMessagesState}
                      stateSnapshots={stateSnapshots}
                      setStateSnapshots={setStateSnapshots}
                      clientName={activeDialogName}
                      clientAvatar={activeDialogAvatar}
                      isClientInfoVisible={isClientInfoVisible}
                      showClientInfoToggle={Boolean(selectedDialogFromList)}
                      onToggleClientInfo={() => setIsClientInfoVisible((prev) => !prev)}
                      onManagerTimerChange={handleManagerTimerChange}
                    />
                  </Flex>
                ) : isDirectDialogLoading ? (
                  <Flex className={styles.chatPane} flex={1}>
                    <LoadingState />
                  </Flex>
                ) : isDirectDialogError ? (
                  <Flex className={styles.chatPane} flex={1}>
                    <FetchingErrorState
                      refetch={dialogMessagesState.handleRefetch}
                      errorMessage={isDirectDialogNotFound ? t('nothingFound') : undefined}
                    />
                  </Flex>
                ) : (
                  <Flex className={styles.chatPane} flex={1}>
                    <NoSelectedDialog />
                  </Flex>
                )}

                {selectedDialogFromList && isClientInfoVisible && (
                  <ClientInfo selectedDialog={selectedDialogFromList} />
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
